import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import JobCard from "./job-card";
import EditJobModal from "./edit-job-modal";
import { Typography } from "@material-ui/core";
import { safetyWiseApi } from "utils/safetyWiseApi";
import {
	Page,
	PageContent,
	Pagination,
	PaginationHeader,
} from "frame/components";
import { getPageResults, canPinJobs } from "utils";
import { useRouter, isNotNullOrEmpty, getCurrentTimestamp } from "utils";

const useStyles = makeStyles((theme) => ({
	root: {
		backgroundColor: theme.palette.white,
	},
	heading: {
		marginTop: theme.spacing(4),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(0),
		},
	},
	body: {
		margin: theme.spacing(3, 0),
	},
	embeddedRoot: {
		maxHeight: "min(70vh, 720px)",
		overflowY: "auto",
	},
	embeddedContent: {
		paddingTop: theme.spacing(1),
	},
	filter: { display: "flex" },
}));

const JobStaffList = ({
	onEdit,
	onDelete,
	currentUserId,
	role,
	hasNetwork,
	pagination,
	onGetJobList,
	flags,
	currentOrganisationGUID,
	variant = "page",
	staffId: staffIdProp,
}) => {
	const classes = useStyles();
	const { match } = useRouter();
	const staffId = staffIdProp || match?.params?.staffId;
	const isEmbedded = variant === "embedded";

	const jobList = getPageResults(pagination);

	const refreshJobList = useCallback(() => {
		const page = pagination?.pageNumber || pagination?.page || 1;
		const pageSize = pagination?.queryPageSize || pagination?.pageSize || 5;
		onGetJobList({
			page,
			pageSize,
			orderBy: "createdAt",
			ascending: false,
			searchKey: "",
			columnName: "staffId",
			columnValue: staffId,
		});
	}, [onGetJobList, pagination, staffId]);

	useEffect(() => {
		if (!staffId || !currentOrganisationGUID) {
			return;
		}
		onGetJobList({
			page: 1,
			pageSize: 5,
			orderBy: "createdAt",
			ascending: false,
			searchKey: "",
			columnName: "staffId",
			columnValue: staffId,
		});
	}, [staffId, currentOrganisationGUID, onGetJobList]);

	const handleToggleStaffPin = useCallback(
		(job) => {
			if (!currentOrganisationGUID || !job) {
				return;
			}
			const nextPinned = !job.pinnedToStaff;
			const body = {
				id: job.id,
				title: job.title || "",
				createdAt: job.createdAt,
				type: job.type || "",
				customerId: job.customerId || "",
				siteId: job.siteId || "",
				staffId: job.staffId || staffId || "",
				notes: job.notes || "",
				attachments: job.attachments || [],
				organisationId: job.organisationId || currentOrganisationGUID,
				latitude: job.latitude ?? "",
				longitude: job.longitude ?? "",
				timestamp: job.timestamp || getCurrentTimestamp(),
				creatorId: job.creatorId || "",
				pinnedToSite: !!job.pinnedToSite,
				pinnedToCustomer: !!job.pinnedToCustomer,
				pinnedToStaff: nextPinned,
			};
			safetyWiseApi
				.post(`${currentOrganisationGUID}/job/createorupdate`, body)
				.then(() => refreshJobList())
				.catch(() => {
					// eslint-disable-next-line no-alert
					window.alert("Could not update pin. Check your connection and try again.");
				});
		},
		[currentOrganisationGUID, staffId, refreshJobList]
	);

	const empty = jobList && jobList.length <= 0;
	const allowPin = canPinJobs(role);

	const listContent = (
		<PageContent pad className={isEmbedded ? classes.embeddedContent : undefined}>
			{!isEmbedded && (
				<Typography variant="h2" className={classes.heading}>
					Photos / Notes
				</Typography>
			)}

			<div className={classes.body}>
				<PaginationHeader pagination={pagination} />

				{isNotNullOrEmpty(jobList) &&
					jobList.map((job, index) => (
						<JobCard
							key={job.id || index}
							job={job}
							role={role}
							currentUserId={currentUserId}
							hasNetwork={hasNetwork}
							onEdit={onEdit}
							onDelete={onDelete}
							enableStaffPin={allowPin}
							onToggleStaffPin={allowPin ? handleToggleStaffPin : undefined}
						/>
					))}
			</div>
			{empty && (
				<Typography variant="body1">
					There are currently no photos or notes.
				</Typography>
			)}

			<Pagination
				disabledOnLoadFetch={flags.create === "processing"}
				onGetList={(paginationParams) =>
					onGetJobList({
						...paginationParams,
						columnName: "staffId",
						columnValue: staffId,
					})
				}
				pagination={pagination}
				orderBy="createdAt"
				ascending={false}
			/>
		</PageContent>
	);

	if (isEmbedded) {
		return (
			<div className={classes.embeddedRoot}>
				{listContent}
				<EditJobModal />
			</div>
		);
	}

	return (
		<Page className={classes.root} title="Photos / Notes">
			{listContent}
			<EditJobModal />
		</Page>
	);
};

JobStaffList.propTypes = {
	currentUserId: PropTypes.string.isRequired,
	role: PropTypes.string.isRequired,
	onEdit: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	hasNetwork: PropTypes.bool.isRequired,
	pagination: PropTypes.object.isRequired,
	flags: PropTypes.object.isRequired,
	onGetJobList: PropTypes.func.isRequired,
	currentOrganisationGUID: PropTypes.string,
	variant: PropTypes.oneOf(["page", "embedded"]),
	staffId: PropTypes.string,
};

export default JobStaffList;
